"use client";

import { CommandDialog, CommandList } from "@layer/ui/components";
import {
	type CommandGroupType,
	findCommandParent,
	flattenCommandGroups,
} from "@layer/utils/lib/commands";
import { useAtomValue, useSetAtom } from "jotai";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useTheme } from "next-themes";
import { useEffect, useMemo, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import type { HotkeysEvent } from "react-hotkeys-hook/dist/types";
import { setUserLocale } from "@/lib/locale/services/locale";
import { setCommandActionAtom } from "@/modules/command-bar/atoms/mutations/command-actions";
import { setCommandStoreAtom } from "@/modules/command-bar/atoms/mutations/command-page";
import {
	isCommandOpenAtom,
	setCommandOpenAtom,
} from "@/modules/command-bar/atoms/states/command-open-state";
import {
	commandPageAtom,
	commandParentAtom,
	selectCommandPageAtom,
} from "@/modules/command-bar/atoms/states/command-page-state";
import { CommandBarBackButton } from "./command-bar-back-button";
import { CommandBarGroup } from "./command-bar-group";
import { CommandBarInput } from "./command-bar-input";

const CommandBarDialog = () => {
	const [searchCommand, setSearchCommand] = useState("");
	const isCommandOpen = useAtomValue(isCommandOpenAtom);
	const commands = useAtomValue(commandPageAtom);
	const setCommandOpen = useSetAtom(setCommandOpenAtom);
	const setCommandStore = useSetAtom(setCommandStoreAtom);
	const commandParent = useAtomValue(commandParentAtom);
	const setCommandAction = useSetAtom(setCommandActionAtom);
	const selectedCommandPage = useAtomValue(selectCommandPageAtom);
	const setSelectCommandPage = useSetAtom(selectCommandPageAtom);
	const pathname = usePathname();
	const router = useRouter();
	const { setTheme } = useTheme();
	const commandGroups = useMemo(
		() => flattenCommandGroups(commands),
		[commands],
	);

	const t = useTranslations("Common");

	const hotkeys = commandGroups
		.flatMap((item) => item?.hotkey?.toLowerCase())
		.filter((item): item is string => item !== undefined)
		.map((item) => `ctrl+${item.split("").join("+")}`);

	useHotkeys(
		hotkeys,
		(_: KeyboardEvent, keysEvent: HotkeysEvent) => {
			const hotkey = keysEvent.keys?.join("");

			const command = commandGroups.find(
				(item) =>
					item?.hotkey &&
					item.hotkey?.toLocaleLowerCase() ===
						hotkey?.toLocaleLowerCase(),
			);

			if (command) {
				handleCommand(command);
			}
		},
		{ enableOnContentEditable: true, enableOnFormTags: ["INPUT"] },
		[commandGroups],
	);

	const handleCommand = (command: CommandGroupType) => {
		const parentGroup = findCommandParent(commands, command.id);

		if (parentGroup && parentGroup.id === "theme") {
			setTheme(command.id);
		}

		if (parentGroup && parentGroup.id === "language") {
			setUserLocale(command.id);
		}

		setCommandAction({ command, router });
		setSearchCommand("");
	};

	useEffect(() => {
		setCommandStore(pathname);
	}, [pathname, setCommandStore]);

	const handleOpenChange = (open: boolean) => {
		setCommandOpen(open);

		if (!open) {
			setSearchCommand("");
			setSelectCommandPage(null);
		}
	};

	return (
		<CommandDialog
			title={t("actions.search")}
			description={t("actions.search")}
			open={isCommandOpen}
			onOpenChange={handleOpenChange}
			commandProps={{
				onKeyDown: (e) => {
					if (!selectedCommandPage) return;

					if (e.key === "Escape") {
						e.preventDefault();
						if (commandParent?.id) {
							setSelectCommandPage(commandParent.id);
						}
					}
				},
			}}
		>
			<CommandBarInput
				searchCommand={searchCommand}
				setSearchCommand={setSearchCommand}
			/>

			<CommandList>
				<CommandBarBackButton />
				<CommandBarGroup handleCommand={handleCommand} />
			</CommandList>
		</CommandDialog>
	);
};

export { CommandBarDialog };
